import { useState, useMemo } from 'react'
import {
  Box, Typography, Chip, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TableSortLabel, Paper,
} from '@mui/material'
import { Inventory2, Search } from '@mui/icons-material'
import ExportMenu from '../../components/common/ExportMenu'
import { formatNumber } from '../../utils/format'

const DEMO_DATA = [
  { item: '10001', description: 'HV CHILI BNS 15oz', location: 'WH-A', onHandCases: 12480, onHandPallets: 149, lastUpdated: '2026-03-10T06:00:00Z' },
  { item: '10002', description: 'FINEST CHILI BNS 15oz', location: 'WH-A', onHandCases: 3024, onHandPallets: 36, lastUpdated: '2026-03-10T06:00:00Z' },
  { item: '10005', description: 'HV CHILI NO BNS 15oz', location: 'WH-A', onHandCases: 8400, onHandPallets: 100, lastUpdated: '2026-03-10T06:00:00Z' },
  { item: '10010', description: 'HV CHICKEN NOODLE 15oz', location: 'WH-B', onHandCases: 15120, onHandPallets: 180, lastUpdated: '2026-03-10T06:00:00Z' },
  { item: '10011', description: 'HV TOMATO SOUP 10.75oz', location: 'WH-B', onHandCases: 18200, onHandPallets: 182, lastUpdated: '2026-03-10T06:00:00Z' },
  { item: '10012', description: 'HV CREAM OF MUSHROOM 10.75oz', location: 'WH-B', onHandCases: 9600, onHandPallets: 96, lastUpdated: '2026-03-10T06:00:00Z' },
  { item: '10020', description: 'HV PORK & BEANS 15oz', location: 'WH-C', onHandCases: 11760, onHandPallets: 140, lastUpdated: '2026-03-10T06:00:00Z' },
  { item: '10021', description: 'HV BAKED BEANS 28oz', location: 'WH-C', onHandCases: 7200, onHandPallets: 120, lastUpdated: '2026-03-10T06:00:00Z' },
  { item: '10030', description: 'GMI PROGRESSO CHKN 19oz', location: 'WH-D', onHandCases: 5040, onHandPallets: 70, lastUpdated: '2026-03-10T06:00:00Z' },
  { item: '10031', description: 'GMI PROGRESSO MINESTRONE 19oz', location: 'WH-D', onHandCases: 4320, onHandPallets: 60, lastUpdated: '2026-03-10T06:00:00Z' },
]

function formatShortDate(iso) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function CurrentInventory() {
  const [search, setSearch] = useState('')
  const [orderBy, setOrderBy] = useState('item')
  const [order, setOrder] = useState('asc')
  const [snapshotDate, setSnapshotDate] = useState('2026-03-10')

  const handleSort = (col) => {
    setOrder(orderBy === col && order === 'asc' ? 'desc' : 'asc')
    setOrderBy(col)
  }

  const rows = useMemo(() => {
    let filtered = DEMO_DATA.filter((r) =>
      `${r.item} ${r.description} ${r.location}`.toLowerCase().includes(search.toLowerCase()),
    )
    filtered.sort((a, b) => {
      const av = a[orderBy], bv = b[orderBy]
      if (av == null || bv == null) return 0
      const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv
      return order === 'asc' ? cmp : -cmp
    })
    return filtered
  }, [search, orderBy, order])

  const columns = [
    { id: 'item', label: 'Item #' },
    { id: 'description', label: 'Description' },
    { id: 'location', label: 'Location' },
    { id: 'onHandCases', label: 'On-Hand (Cases)', align: 'right' },
    { id: 'onHandPallets', label: 'On-Hand (Pallets)', align: 'right' },
    { id: 'lastUpdated', label: 'Last Updated' },
  ]

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Inventory2 color="primary" />
        <Typography variant="h3" sx={{ flex: 1 }}>Current Inventory</Typography>
        <ExportMenu />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip label="SAP Inventory" size="small" variant="outlined" />
        <Typography variant="overline" color="text.secondary">
          As of Mar 10, 2026 · v14 · {DEMO_DATA.length} items
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search items…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 280 }}
          slotProps={{ input: { startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> } }}
        />
        <TextField
          size="small"
          type="date"
          label="Snapshot Date"
          value={snapshotDate}
          onChange={(e) => setSnapshotDate(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ width: 180 }}
        />
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.id} align={col.align || 'left'} sx={{ fontWeight: 700 }}>
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : 'asc'}
                    onClick={() => handleSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.item} hover>
                <TableCell sx={{ fontWeight: 600 }}>{row.item}</TableCell>
                <TableCell>{row.description}</TableCell>
                <TableCell>{row.location}</TableCell>
                <TableCell align="right">{formatNumber(row.onHandCases)}</TableCell>
                <TableCell align="right">{formatNumber(row.onHandPallets)}</TableCell>
                <TableCell>{formatShortDate(row.lastUpdated)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
